/**
 * Formats user input into normalized upper-case serial number format.
 * Converts characters to A-Z, 0-9, space, dash, slash.
 */
export const normalizeSerialNumberInput = (value: string): string => {
  return value.toUpperCase().trim();
};

/**
 * Validates a serial number format according to YND domain rules:
 * - 4-64 chars
 * - Allowed chars: A-Z, 0-9, space, '-', '/'
 * - No 4+ consecutive identical characters (e.g. 'AAAA')
 * - No 4+ consecutive sequential numbers (e.g. '1234', '9876')
 *
 * @returns null if valid (or empty), or error message string if invalid.
 */
export const validateSerialNumberFormat = (
  value?: string | null
): string | null => {
  if (!value) return null;

  const normalized = value.trim().toUpperCase();
  if (normalized.length === 0) return null;

  if (normalized.length < 4 || normalized.length > 64) {
    return 'Seri numarası 4 ile 64 karakter arasında olmalıdır.';
  }

  const allowedRegex = /^[A-Z0-9\s\-\/]+$/;
  if (!allowedRegex.test(normalized)) {
    return 'Seri numarası sadece büyük harf, rakam, boşluk, "-" ve "/" içerebilir.';
  }

  // Check 4+ consecutive identical characters
  if (/(.)\1{3,}/.test(normalized)) {
    return 'Seri numarası aynı karakteri 4 kereden fazla ardışık içeremez.';
  }

  // Check 4+ consecutive sequential ascending or descending digits
  if (hasSequentialDigits(normalized)) {
    return 'Seri numarası ardışık sıralı rakamlar (örn. 1234, 9876) içeremez.';
  }

  return null;
};

const hasSequentialDigits = (str: string): boolean => {
  const digitsOnly = str.replace(/[^0-9]/g, '');
  if (digitsOnly.length < 4) return false;

  for (let i = 0; i <= digitsOnly.length - 4; i++) {
    const d1 = Number(digitsOnly[i]);
    const d2 = Number(digitsOnly[i + 1]);
    const d3 = Number(digitsOnly[i + 2]);
    const d4 = Number(digitsOnly[i + 3]);

    // Ascending: 1,2,3,4
    if (d2 === d1 + 1 && d3 === d2 + 1 && d4 === d3 + 1) return true;
    // Descending: 4,3,2,1
    if (d2 === d1 - 1 && d3 === d2 - 1 && d4 === d3 - 1) return true;
  }

  return false;
};
